import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomCheckBox } from 'components/atoms/CustomCheckbox';
import TableFilterCalendar from 'components/molecules/TableFilterCalendar';
import TableFilterMultiSelect from 'components/molecules/TableFilterMultiSelect';
import { CustomColumn } from 'components/molecules/ResponsiveHistoryTable';
import {
  Option,
  ShipmentRegistrationFilter,
  ShipmentRegistrationRow,
} from 'util/Types';
import styles from './ShipmentRegistration.module.scss';

type Params = {
  checkedIds: string[];
  convertedCompressions: ShipmentRegistrationRow[];
  filter: ShipmentRegistrationFilter;
  materialNameOptions: Option[];
  gradeOptions: Option[];
  typeOptions: Option[];
  selectedMaterialName: string | null;
  onSelectAll: () => void;
  onCheckItem: (id: string) => void;
  onFilterChange: (value: ShipmentRegistrationFilter) => void;
};

export const useProcessingColumns = ({
  checkedIds,
  convertedCompressions,
  filter,
  materialNameOptions,
  gradeOptions,
  typeOptions,
  selectedMaterialName,
  onSelectAll,
  onCheckItem,
  onFilterChange,
}: Params): CustomColumn<ShipmentRegistrationRow>[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        Header: (() => {
          const hasMixedMaterials =
            selectedMaterialName === null &&
            new Set(convertedCompressions.map((r) => r.materialName)).size > 1;

          if (hasMixedMaterials) return <></>;

          const selectableCount = selectedMaterialName
            ? convertedCompressions.filter(
                (r) => r.materialName === selectedMaterialName
              ).length
            : convertedCompressions.length;

          return (
            <span
              onClick={onSelectAll}
              style={{
                cursor:
                  convertedCompressions.length > 0 ? 'pointer' : 'not-allowed',
                color: convertedCompressions.length > 0 ? 'inherit' : 'gray',
              }}
            >
              {t(
                selectableCount === checkedIds.length && selectableCount > 0
                  ? 'common.deselect_all'
                  : 'common.select_all'
              )}
            </span>
          );
        })(),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        accessor: 'checkbox' as any,
        className: styles.checkboxColumn,
        Cell: ({ row }) => {
          const isDisabled =
            selectedMaterialName !== null &&
            row.original.materialName !== selectedMaterialName;

          if (isDisabled) return null;

          return (
            <CustomCheckBox
              id={row.original.id}
              darkMode
              checked={checkedIds.includes(row.original.id)}
              onChange={() => onCheckItem(row.original.id)}
            />
          );
        },
      },
      {
        Header: (
          <TableFilterCalendar
            label={
              t(
                'compressions.shipment.shipment_registration.columns.processing_datetime'
              ) || ''
            }
            value={filter?.datetime}
            onChange={(value) => onFilterChange({ ...filter, datetime: value })}
            labelClassName={styles.tableFilterLabel}
            endDateRequired
          />
        ),
        accessor: 'processingDatetime',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t(
            'compressions.shipment.shipment_registration.columns.processing_id'
          ) || '',
        accessor: 'id',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t(
            'compressions.shipment.shipment_registration.columns.person_in_charge'
          ) || '',
        accessor: 'personInCharge',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: (
          <TableFilterMultiSelect
            label={
              t(
                'compressions.shipment.shipment_registration.columns.material_name'
              ) || ''
            }
            options={materialNameOptions}
            values={filter?.materialName}
            onChange={(value) =>
              onFilterChange({ ...filter, materialName: value })
            }
            classNames={{ label: styles.tableFilterLabel }}
          />
        ),
        accessor: 'materialName',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: (
          <TableFilterMultiSelect
            label={
              t('compressions.shipment.shipment_registration.columns.grade') ||
              ''
            }
            options={gradeOptions}
            values={filter?.grade}
            onChange={(value) => onFilterChange({ ...filter, grade: value })}
            classNames={{ label: styles.tableFilterLabel }}
          />
        ),
        accessor: 'grade',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header: (
          <TableFilterMultiSelect
            label={
              t('compressions.shipment.shipment_registration.columns.kind') ||
              ''
            }
            options={typeOptions}
            values={filter?.kind}
            onChange={(value) => onFilterChange({ ...filter, kind: value })}
            classNames={{ label: styles.tableFilterLabel }}
          />
        ),
        accessor: 'kind',
        Cell: ({ value }) => <>{value}</>,
      },
      {
        Header:
          t('compressions.shipment.shipment_registration.columns.weight') || '',
        accessor: 'weight',
        Cell: ({ value }) => <>{`${value}kg`}</>,
      },
    ],
    [
      t,
      checkedIds,
      convertedCompressions,
      filter,
      materialNameOptions,
      gradeOptions,
      typeOptions,
      selectedMaterialName,
    ]
  );
};
